// src/pages/MyAccount/MyAccount.js
import React, { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Chip,
  Card,
  CardActionArea,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox
} from '@mui/material'
import {
  PhotoCamera,
  Visibility,
  VisibilityOff,
  Save,
  BusinessCenter,
  Verified,
  Person,
  Security,
  ContentCopy,
  Link as LinkIcon,
  Home,
  CheckCircle,
  CloudUpload
} from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { useAuth } from '../../context/AuthContext'
import api, { userAPI } from '../../services/api'
import { mensagemDeErro } from '../../services/erros'

const MyAccount = () => {
  const { user, updateUser } = useAuth()
  const location = useLocation()
  const [loading, setLoading] = useState(false)
  const [loadingProfile, setLoadingProfile] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [activeTab, setActiveTab] = useState(0)

  // Dados pessoais
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    cpf: '',
    is_realtor: false,
    creci: '',
    cnpj: '',
    company_name: '',
    business_phone: ''
  })

  // Senha
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  })
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  // Avatar
  const [avatarPreview, setAvatarPreview] = useState(null)
  const [avatarFile, setAvatarFile] = useState(null)

  // Importação XML (token CRM)
  const [importUrl, setImportUrl] = useState('')
  const [loadingToken, setLoadingToken] = useState(false)

  // Upload manual XML
  const [xmlFile, setXmlFile] = useState(null)
  const [uploadLoading, setUploadLoading] = useState(false)
  const [uploadResult, setUploadResult] = useState(null)
  const [fileInputKey, setFileInputKey] = useState(0)
  const [previewData, setPreviewData] = useState(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())

  // Deep-link: abre aba XML se URL contém ?tab=xml
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    if (params.get('tab') === 'xml' && (user?.is_realtor || user?.is_agency)) {
      setActiveTab(2)
    }
  }, [location.search, user])

  // Carregar dados do perfil do backend
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setLoadingProfile(true)
        const response = await api.get('/api/user/profile/')
        const userData = response.data

        setFormData({
          first_name: userData.first_name || '',
          last_name: userData.last_name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          cpf: userData.cpf || '',
          is_realtor: userData.is_realtor || false,
          creci: userData.creci || '',
          cnpj: userData.cnpj || '',
          company_name: userData.company_name || '',
          business_phone: userData.business_phone || ''
        })

        updateUser(userData)

        if (userData.avatar) {
          setAvatarPreview(userData.avatar)
        }
      } catch (err) {
        console.error('Erro ao carregar perfil:', err)
        setError('Erro ao carregar dados do perfil')
      } finally {
        setLoadingProfile(false)
      }
    }

    fetchUserProfile()
  }, [])

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    })
  }

  const handleAvatarChange = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('A imagem deve ter no máximo 2MB')
        return
      }
      setAvatarFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleUpdateProfile = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    try {
      const formDataToSend = new FormData()

      // Enviar TODOS os campos, inclusive os vazios. Antes, `else if
      // (formData[key])` descartava qualquer valor falsy - entao apagar o CPF
      // no formulario nao apagava nada: o campo simplesmente nao ia, o
      // servidor mantinha o valor antigo e a tela dizia "atualizado com
      // sucesso". String vazia em campo unico (CPF, CNPJ) vira NULL no
      // servidor, ver user_profile_view.
      Object.keys(formData).forEach((key) => {
        const valor = formData[key]
        formDataToSend.append(key, valor === null || valor === undefined ? '' : valor)
      })

      if (avatarFile) {
        formDataToSend.append('avatar', avatarFile)
      }

      // PATCH, nao PUT. O formulario edita uma PARTE do usuario, e no DRF
      // PUT significa "substitua o recurso inteiro": com partial=False ele
      // exige todo campo obrigatorio do modelo. Quem tivesse o telefone em
      // branco levava 400 e nao conseguia salvar mais nada, nunca.
      const response = await api.patch('/api/user/profile/', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (response.data) {
        updateUser(response.data)
        setSuccess('Perfil atualizado com sucesso!')
        setAvatarFile(null)
        setTimeout(() => setSuccess(''), 5000)
      }
    } catch (err) {
      console.error('Erro ao atualizar perfil:', err)
      setError(mensagemDeErro(err, 'Erro ao atualizar perfil'))
    } finally {
      setLoading(false)
    }
  }

  const handleUpdatePassword = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setSuccess('')

    if (!passwordData.current_password) {
      setError('Senha atual é obrigatória')
      setLoading(false)
      return
    }

    if (!passwordData.new_password) {
      setError('Nova senha é obrigatória')
      setLoading(false)
      return
    }

    if (passwordData.new_password.length < 8) {
      setError('Nova senha deve ter no mínimo 8 caracteres')
      setLoading(false)
      return
    }

    if (!/\d/.test(passwordData.new_password)) {
      setError('Nova senha deve conter pelo menos 1 número')
      setLoading(false)
      return
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('Nova senha e confirmação não coincidem')
      setLoading(false)
      return
    }

    try {
      await api.put('/api/user/change-password/', {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      })

      setSuccess('Senha alterada com sucesso!')
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      })
      setTimeout(() => setSuccess(''), 5000)
    } catch (err) {
      console.error('Erro ao alterar senha:', err)
      setError(mensagemDeErro(err, 'Erro ao alterar senha. Verifique se a senha atual está correta.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 2) return
    const fetchImportToken = async () => {
      setLoadingToken(true)
      try {
        const { data } = await api.get('/api/user/import-token/')
        setImportUrl(data.import_url)
      } catch (err) {
        setError('Erro ao carregar URL de importação')
      } finally {
        setLoadingToken(false)
      }
    }
    fetchImportToken()
  }, [activeTab])

  const handleResetToken = async () => {
    setLoadingToken(true)
    setError('')
    setSuccess('')
    try {
      const { data } = await api.post('/api/user/import-token/reset/')
      setImportUrl(data.import_url)
      setSuccess('Token regenerado. Atualize seu CRM com a nova URL.')
    } catch (err) {
      setError('Erro ao regenerar token')
    } finally {
      setLoadingToken(false)
    }
  }

  const handleXmlPreview = async (file) => {
    setPreviewLoading(true)
    setPreviewData(null)
    setUploadResult(null)
    setError('')
    try {
      const { data } = await userAPI.previewXML(file)
      setPreviewData(data)
      setSelectedIds(new Set(data.listings.map((l) => l.listing_id)))
    } catch (err) {
      setError(mensagemDeErro(err, 'Erro ao ler o arquivo XML.'))
    } finally {
      setPreviewLoading(false)
    }
  }

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(new Set(previewData.listings.map((l) => l.listing_id)))
    } else {
      setSelectedIds(new Set())
    }
  }

  const handleXmlUpload = async () => {
    if (!xmlFile) {
      setError('Selecione um arquivo XML antes de enviar.')
      return
    }
    if (selectedIds.size === 0) {
      setError('Selecione ao menos um imóvel para importar.')
      return
    }
    setUploadLoading(true)
    setError('')
    setSuccess('')
    setUploadResult(null)
    try {
      const { data } = await userAPI.uploadXML(xmlFile, Array.from(selectedIds))
      setUploadResult(data)
      setXmlFile(null)
      setPreviewData(null)
      setSelectedIds(new Set())
      setFileInputKey((k) => k + 1)
    } catch (err) {
      setError(mensagemDeErro(err, 'Erro ao importar o arquivo XML.'))
    } finally {
      setUploadLoading(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue)
    setError('')
    setSuccess('')
  }

  if (!user) {
    return null
  }

  if (loadingProfile) {
    return (
      <Container
        maxWidth='md'
        sx={{ py: 4, display: 'flex', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    )
  }

  return (
    <Container maxWidth='md' sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ overflow: 'hidden' }}>
        {/* Header com gradiente */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #FF7F27 0%, #FF9A52 100%)',
            p: 4,
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              src={avatarPreview}
              sx={{
                width: 80,
                height: 80,
                mr: 3,
                border: '4px solid white',
                fontSize: '2rem',
                bgcolor: 'rgba(255,255,255,0.3)'
              }}
            >
              {!avatarPreview && (user.email?.[0]?.toUpperCase() || 'U')}
            </Avatar>
            <Box>
              <Typography variant='h4' fontWeight='bold' sx={{ mb: 0.5 }}>
                {user.email}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {user.is_verified && (
                  <Chip
                    icon={<Verified />}
                    label='Verificado'
                    size='small'
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                )}
                {user.is_realtor && (
                  <Chip
                    icon={<BusinessCenter />}
                    label='Corretor'
                    size='small'
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                )}
                {user.is_agency && (
                  <Chip
                    icon={<BusinessCenter />}
                    label='Imobiliária'
                    size='small'
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.2)',
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            px: 3,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': {
              textTransform: 'none',
              fontSize: '1rem',
              fontWeight: 600
            }
          }}
        >
          <Tab icon={<Person />} iconPosition='start' label='Dados Pessoais' />
          <Tab icon={<Security />} iconPosition='start' label='Alterar Senha' />
          {(user.is_realtor || user.is_agency) && (
            <Tab icon={<LinkIcon />} iconPosition='start' label='Integração XML' />
          )}
        </Tabs>

        <Box sx={{ p: 4 }}>
          {error && (
            <Alert severity='error' sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert
              severity='success'
              sx={{ mb: 3 }}
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          )}

          {/* Tab 0: Dados Pessoais */}
          {activeTab === 0 && (
            <Box component='form' onSubmit={handleUpdateProfile}>
              <Box sx={{ mb: 4 }}>
                <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
                  Foto do Perfil
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Avatar
                    src={avatarPreview}
                    sx={{
                      width: 100,
                      height: 100,
                      mr: 3,
                      bgcolor: 'primary.main',
                      fontSize: '2.5rem'
                    }}
                  >
                    {!avatarPreview && (user.email?.[0]?.toUpperCase() || 'U')}
                  </Avatar>
                  <Box>
                    <Button
                      variant='outlined'
                      component='label'
                      startIcon={<PhotoCamera />}
                    >
                      Alterar Foto
                      <input
                        type='file'
                        hidden
                        accept='image/*'
                        onChange={handleAvatarChange}
                      />
                    </Button>
                    <Typography
                      variant='caption'
                      display='block'
                      sx={{ mt: 1 }}
                    >
                      Imagem de até 2MB (JPG, PNG)
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Typography variant='h6' sx={{ mb: 2, fontWeight: 600 }}>
                Informações Básicas
              </Typography>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label='Nome'
                    name='first_name'
                    value={formData.first_name}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label='Sobrenome'
                    name='last_name'
                    value={formData.last_name}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label='Email'
                    name='email'
                    type='email'
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    required
                    fullWidth
                    label='Telefone'
                    name='phone'
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder='(11) 99999-9999'
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label='CPF'
                    name='cpf'
                    value={formData.cpf}
                    onChange={handleInputChange}
                    placeholder='000.000.000-00'
                    helperText='Opcional'
                  />
                </Grid>

                {!user.is_agency && (
                  <Grid size={{ xs: 12 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant='h6' sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
                      Tipo de Perfil
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          variant='outlined'
                          sx={{
                            borderColor: !formData.is_realtor ? 'primary.main' : 'divider',
                            borderWidth: !formData.is_realtor ? 2 : 1,
                            bgcolor: !formData.is_realtor ? 'primary.50' : 'background.paper',
                            transition: 'all 0.2s'
                          }}
                        >
                          <CardActionArea
                            onClick={() => setFormData({ ...formData, is_realtor: false })}
                          >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                              <Home color={!formData.is_realtor ? 'primary' : 'action'} />
                              <Box sx={{ flex: 1 }}>
                                <Typography fontWeight={600}>Proprietário</Typography>
                                <Typography variant='caption' color='text.secondary'>
                                  Anuncie seus imóveis diretamente
                                </Typography>
                              </Box>
                              {!formData.is_realtor && (
                                <CheckCircle color='primary' fontSize='small' />
                              )}
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>

                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Card
                          variant='outlined'
                          sx={{
                            borderColor: formData.is_realtor ? 'primary.main' : 'divider',
                            borderWidth: formData.is_realtor ? 2 : 1,
                            bgcolor: formData.is_realtor ? 'primary.50' : 'background.paper',
                            transition: 'all 0.2s'
                          }}
                        >
                          <CardActionArea
                            onClick={() => setFormData({ ...formData, is_realtor: true })}
                          >
                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                              <BusinessCenter color={formData.is_realtor ? 'primary' : 'action'} />
                              <Box sx={{ flex: 1 }}>
                                <Typography fontWeight={600}>Corretor</Typography>
                                <Typography variant='caption' color='text.secondary'>
                                  Profissional com registro CRECI
                                </Typography>
                              </Box>
                              {formData.is_realtor && (
                                <CheckCircle color='primary' fontSize='small' />
                              )}
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    </Grid>
                  </Grid>
                )}

                {formData.is_realtor && (
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label='CRECI'
                      name='creci'
                      value={formData.creci}
                      onChange={handleInputChange}
                      placeholder='12345-F'
                      helperText='Obrigatório para corretores'
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position='start'>
                            <BusinessCenter />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                )}

                {user.is_agency && (
                  <>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label='CNPJ'
                        name='cnpj'
                        value={formData.cnpj}
                        onChange={handleInputChange}
                        placeholder='00.000.000/0000-00'
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position='start'>
                              <BusinessCenter />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label='Razão Social'
                        name='company_name'
                        value={formData.company_name}
                        onChange={handleInputChange}
                        placeholder='Nome da empresa'
                      />
                    </Grid>

                    <Grid size={{ xs: 12 }}>
                      <TextField
                        fullWidth
                        label='Telefone Comercial'
                        name='business_phone'
                        value={formData.business_phone}
                        onChange={handleInputChange}
                        placeholder='(11) 3333-4444'
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              <Button
                type='submit'
                variant='contained'
                size='large'
                startIcon={<Save />}
                sx={{ mt: 4 }}
                disabled={loading}
              >
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </Box>
          )}

          {/* Tab 1: Alterar Senha */}
          {activeTab === 1 && (
            <Box component='form' onSubmit={handleUpdatePassword}>
              <Typography variant='h6' sx={{ mb: 3, fontWeight: 600 }}>
                Alterar Senha
              </Typography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    label='Senha Atual'
                    name='current_password'
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={handlePasswordChange}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() =>
                              setShowCurrentPassword(!showCurrentPassword)
                            }
                            edge='end'
                          >
                            {showCurrentPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    label='Nova Senha'
                    name='new_password'
                    type={showNewPassword ? 'text' : 'password'}
                    value={passwordData.new_password}
                    onChange={handlePasswordChange}
                    helperText='Mínimo 8 caracteres, incluindo pelo menos 1 número'
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge='end'
                          >
                            {showNewPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    required
                    fullWidth
                    label='Confirmar Nova Senha'
                    name='confirm_password'
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={passwordData.confirm_password}
                    onChange={handlePasswordChange}
                    helperText='Digite a nova senha novamente'
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>
                          <IconButton
                            onClick={() =>
                              setShowConfirmPassword(!showConfirmPassword)
                            }
                            edge='end'
                          >
                            {showConfirmPassword ? (
                              <VisibilityOff />
                            ) : (
                              <Visibility />
                            )}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
              </Grid>

              <Button
                type='submit'
                variant='contained'
                size='large'
                startIcon={<Save />}
                sx={{ mt: 4 }}
                disabled={loading}
              >
                {loading ? 'Alterando...' : 'Alterar Senha'}
              </Button>
            </Box>
          )}

          {/* Tab 2: Integração XML */}
          {activeTab === 2 && (user.is_realtor || user.is_agency) && (
            <Box>
              <Typography variant='h6' sx={{ mb: 1, fontWeight: 600 }}>
                Importação XML (VRSync)
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                Cadastre a URL abaixo no seu CRM/ERP (Simob, Flexpro, etc.) para
                sincronizar seus imóveis automaticamente via POST XML.
              </Typography>

              {loadingToken ? (
                <CircularProgress size={24} />
              ) : importUrl ? (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      mb: 2
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{ flexGrow: 1, fontFamily: 'monospace', wordBreak: 'break-all' }}
                    >
                      {importUrl}
                    </Typography>
                    <Tooltip title='Copiar URL'>
                      <IconButton
                        size='small'
                        onClick={() => {
                          navigator.clipboard.writeText(importUrl)
                          setSuccess('URL copiada!')
                        }}
                      >
                        <ContentCopy fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Alert severity='warning' sx={{ mb: 2 }}>
                    Ao regenerar, a URL anterior é <strong>invalidada imediatamente</strong>.
                    Atualize seu CRM após o reset.
                  </Alert>

                  <Button
                    variant='outlined'
                    color='warning'
                    onClick={handleResetToken}
                    disabled={loadingToken}
                  >
                    Regenerar Token
                  </Button>
                </>
              ) : null}

              <Divider sx={{ my: 3 }} />

              <Typography variant='h6' sx={{ mb: 1, fontWeight: 600 }}>
                Upload Manual de XML
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Envie um arquivo .xml no formato VRSync (VivaReal) ou ZAP para importar imóveis em
                massa. Limite: 10 MB.
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <Button
                  variant='outlined'
                  component='label'
                  startIcon={
                    previewLoading ? <CircularProgress size={16} color='inherit' /> : <CloudUpload />
                  }
                  disabled={uploadLoading || previewLoading}
                >
                  Selecionar arquivo XML
                  <input
                    key={fileInputKey}
                    type='file'
                    hidden
                    accept='.xml'
                    onChange={(e) => {
                      const file = e.target.files[0] || null
                      setXmlFile(file)
                      setUploadResult(null)
                      if (file) handleXmlPreview(file)
                    }}
                  />
                </Button>
                {xmlFile && !previewLoading && (
                  <Typography variant='body2' color='text.secondary'>
                    {xmlFile.name} ({(xmlFile.size / 1024).toFixed(1)} KB)
                  </Typography>
                )}
                {previewLoading && (
                  <Typography variant='body2' color='text.secondary'>
                    Lendo arquivo…
                  </Typography>
                )}
              </Box>

              {previewData && !previewLoading && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    {previewData.count} imóvel(is) encontrado(s) —{' '}
                    <strong>{selectedIds.size} selecionado(s)</strong> para importar:
                  </Typography>
                  <TableContainer
                    component={Paper}
                    variant='outlined'
                    sx={{ maxHeight: 360, overflow: 'auto' }}
                  >
                    <Table size='small' stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding='checkbox'>
                            <Checkbox
                              size='small'
                              checked={selectedIds.size === previewData.count}
                              indeterminate={
                                selectedIds.size > 0 && selectedIds.size < previewData.count
                              }
                              onChange={(e) => handleSelectAll(e.target.checked)}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Título</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Tipo</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Transação</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Cidade/UF</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Preço</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align='center'>Qtos</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align='center'>Área</TableCell>
                          <TableCell sx={{ fontWeight: 700 }} align='center'>Fotos</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {previewData.listings.map((listing, idx) => {
                          const isSelected = selectedIds.has(listing.listing_id)
                          return (
                            <TableRow
                              key={listing.listing_id || idx}
                              hover
                              selected={isSelected}
                              onClick={() => handleToggleSelect(listing.listing_id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding='checkbox'>
                                <Checkbox
                                  size='small'
                                  checked={isSelected}
                                  onChange={() => handleToggleSelect(listing.listing_id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell sx={{ maxWidth: 200 }}>
                                <Typography variant='body2' noWrap title={listing.title}>
                                  {listing.title}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={
                                    {
                                      apartamento: 'Apto',
                                      casa: 'Casa',
                                      terreno: 'Terreno',
                                      comercial: 'Comercial',
                                      rural: 'Rural',
                                    }[listing.property_type] || listing.property_type
                                  }
                                  size='small'
                                  variant='outlined'
                                />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={listing.transaction_type === 'venda' ? 'Venda' : 'Aluguel'}
                                  size='small'
                                  color={
                                    listing.transaction_type === 'venda' ? 'primary' : 'secondary'
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {listing.city}
                                {listing.state ? `/${listing.state}` : ''}
                              </TableCell>
                              <TableCell>
                                {listing.price_on_request
                                  ? 'A combinar'
                                  : listing.price
                                  ? new Intl.NumberFormat('pt-BR', {
                                      style: 'currency',
                                      currency: 'BRL',
                                      maximumFractionDigits: 0,
                                    }).format(listing.price)
                                  : '—'}
                              </TableCell>
                              <TableCell align='center'>{listing.bedrooms || '—'}</TableCell>
                              <TableCell align='center'>
                                {listing.area > 0 ? `${listing.area} m²` : '—'}
                              </TableCell>
                              <TableCell align='center'>{listing.image_count}</TableCell>
                            </TableRow>
                          )
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Button
                variant='contained'
                startIcon={
                  uploadLoading ? (
                    <CircularProgress size={18} color='inherit' />
                  ) : (
                    <CloudUpload />
                  )
                }
                onClick={handleXmlUpload}
                disabled={!xmlFile || uploadLoading || previewLoading || selectedIds.size === 0}
                sx={{ mb: 3 }}
              >
                {uploadLoading
                  ? 'Importando…'
                  : selectedIds.size > 0
                  ? `Importar ${selectedIds.size} imóvel(is)`
                  : 'Importar XML'}
              </Button>

              {uploadResult && (
                <Alert
                  severity={uploadResult.errors?.length > 0 ? 'warning' : 'success'}
                  sx={{ mb: 2 }}
                >
                  <Typography variant='body2' fontWeight={600}>
                    Importação concluída
                  </Typography>
                  <Typography variant='body2'>
                    {uploadResult.imported} importado(s) &nbsp;·&nbsp;
                    {uploadResult.updated} atualizado(s) &nbsp;·&nbsp;
                    {uploadResult.skipped} com erro(s)
                  </Typography>
                  {uploadResult.errors?.length > 0 && (
                    <Box sx={{ maxHeight: 160, overflowY: 'auto', mt: 1 }}>
                      {uploadResult.errors.map((e, i) => (
                        <Typography key={i} variant='caption' display='block' color='error'>
                          • {e.listing_id ? `[${e.listing_id}] ` : ''}
                          {e.reason}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Container>
  )
}

export default MyAccount
